import { useParams, Link } from "react-router";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";

export function JournalDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { journal } = useAdminData();
  const { lang, t } = useLanguage();
  const theme = useTheme();

  const entry = journal.find((j) => j.slug === slug && j.published);

  if (!entry) {
    return (
      <div className="pt-28 pb-24 text-center px-6">
        <h1
          className="tracking-[0.1em] mb-4"
          style={{ fontFamily: theme.fontHeading, fontSize: "32px", fontWeight: 400, color: theme.fg }}
        >
          {t("journal.not_found")}
        </h1>
        <p
          className="mb-8"
          style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
        >
          {t("journal.not_found_desc")}
        </p>
        <Link
          to="/journal"
          className="tracking-[0.15em] uppercase border-b pb-0.5 transition-colors"
          style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.primary, borderColor: `${theme.primary}4d` }}
        >
          {t("journal.back")}
        </Link>
      </div>
    );
  }

  // Get other published entries for "More Stories"
  const otherEntries = journal.filter((j) => j.published && j.id !== entry.id).slice(0, 2);

  return (
    <div className="pt-28 pb-24">
      {/* Back link */}
      <div className="max-w-3xl mx-auto px-6 mb-10">
        <Link
          to="/journal"
          className="tracking-[0.15em] uppercase border-b pb-0.5 transition-colors inline-flex items-center gap-2"
          style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.primary, borderColor: `${theme.primary}4d` }}
        >
          &larr; {t("journal.back")}
        </Link>
      </div>

      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 mb-10 text-center">
        <p
          className="tracking-[0.3em] uppercase mb-3"
          style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.accent }}
        >
          {entry.author} &middot;{" "}
          {new Date(entry.created_at).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </p>
        <h1
          className="tracking-[0.06em]"
          style={{
            fontFamily: theme.fontHeading,
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 400,
            color: theme.fg,
          }}
        >
          {localized(entry, "title", lang)}
        </h1>
      </div>

      {/* Hero image */}
      {entry.image && (
        <div className="max-w-4xl mx-auto px-6 mb-12">
          <div className="overflow-hidden rounded-lg" style={{ backgroundColor: `${theme.border}40` }}>
            <ImageWithFallback
              src={entry.image}
              alt={entry.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6">
        <div
          className="leading-[2] whitespace-pre-line"
          style={{
            fontFamily: theme.fontBody,
            fontSize: "15px",
            fontWeight: 300,
            color: theme.muted,
          }}
        >
          {localized(entry, "content", lang)}
        </div>
      </div>

      {/* Divider */}
      <div className="max-w-3xl mx-auto px-6 my-16">
        <div className="h-px" style={{ backgroundColor: theme.border }} />
      </div>

      {/* More Stories */}
      {otherEntries.length > 0 && (
        <div className="max-w-4xl mx-auto px-6">
          <h2
            className="tracking-[0.1em] text-center mb-10"
            style={{
              fontFamily: theme.fontHeading,
              fontSize: "clamp(22px, 3vw, 28px)",
              fontWeight: 400,
              color: theme.fg,
            }}
          >
            {t("journal.more_stories")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {otherEntries.map((other) => (
              <Link
                key={other.id}
                to={`/journal/${other.slug}`}
                className="group"
              >
                <div className="overflow-hidden rounded-lg mb-4" style={{ backgroundColor: `${theme.border}40` }}>
                  {other.image && (
                    <ImageWithFallback
                      src={other.image}
                      alt={other.title}
                      className="w-full aspect-[4/3] object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  )}
                </div>
                <p
                  className="tracking-[0.2em] uppercase mb-1"
                  style={{ fontFamily: theme.fontBody, fontSize: "10px", color: theme.accent }}
                >
                  {other.author}
                </p>
                <h3
                  className="tracking-[0.04em] transition-colors"
                  style={{
                    fontFamily: theme.fontHeading,
                    fontSize: "20px",
                    fontWeight: 400,
                    color: theme.fg,
                  }}
                >
                  {localized(other, "title", lang)}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
